/**
 * Error Logging Utilities
 *
 * Utility classes for data sanitization, rate limiting, and context collection
 */

import {
  ErrorContext,
  ErrorBreadcrumb,
  PerformanceMetrics,
  IDataSanitizer,
  IErrorRateLimit,
} from '@shared/types/ErrorLogging';
import {
  SENSITIVE_DATA_PATTERNS,
  SENSITIVE_URL_PARAMS,
} from '@shared/config/errorLogging';

/**
 * Data sanitization utility to remove sensitive information
 */
export class DataSanitizer implements IDataSanitizer {
  /**
   * Sanitizes an object by removing sensitive data
   */
  sanitizeObject(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (typeof obj === 'string') {
      return this.sanitizeString(obj);
    }

    if (typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeObject(item));
    }

    const sanitized: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (this.isSensitiveKey(key)) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = this.sanitizeObject(value);
      }
    }

    return sanitized;
  }

  /**
   * Sanitizes URL by removing sensitive parameters
   */
  sanitizeUrl(url: string): string {
    try {
      const urlObj = new URL(url);

      SENSITIVE_URL_PARAMS.forEach(param => {
        if (urlObj.searchParams.has(param)) {
          urlObj.searchParams.set(param, '[REDACTED]');
        }
      });

      return urlObj.toString();
    } catch {
      return url; // Return original if URL parsing fails
    }
  }

  /**
   * Sanitizes HTTP headers
   */
  sanitizeHeaders(headers: Record<string, string>): Record<string, string> {
    const sanitized: Record<string, string> = {};

    for (const [key, value] of Object.entries(headers)) {
      if (this.isSensitiveKey(key)) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  private isSensitiveKey(key: string): boolean {
    return SENSITIVE_DATA_PATTERNS.some(pattern => pattern.test(key));
  }

  private sanitizeString(str: string): string {
    // Basic sanitization for potentially sensitive strings
    if (str.length > 1000) {
      return str.substring(0, 1000) + '...[TRUNCATED]';
    }
    return str;
  }
}

/**
 * Rate limiting utility to prevent spam logging
 */
export class ErrorRateLimit implements IErrorRateLimit {
  private errorCounts = new Map<string, { count: number; resetTime: number }>();
  private readonly maxErrorsPerMinute: number;
  private readonly windowMs = 60 * 1000; // 1 minute

  constructor(maxErrorsPerMinute: number = 10) {
    this.maxErrorsPerMinute = maxErrorsPerMinute;
  }

  /**
   * Checks if an error should be logged based on rate limits
   */
  shouldLogError(error: Error): boolean {
    const errorKey = this.getErrorKey(error);
    const now = Date.now();
    const entry = this.errorCounts.get(errorKey);

    if (!entry || now > entry.resetTime) {
      // First occurrence or window expired
      this.errorCounts.set(errorKey, {
        count: 1,
        resetTime: now + this.windowMs,
      });
      return true;
    }

    if (entry.count >= this.maxErrorsPerMinute) {
      return false; // Rate limit exceeded
    }

    entry.count++;
    return true;
  }

  /**
   * Resets all rate limiting counters
   */
  reset(): void {
    this.errorCounts.clear();
  }

  private getErrorKey(error: Error): string {
    // Create a unique key based on error type and message
    return `${error.name}:${error.message}:${this.getStackSignature(error)}`;
  }

  private getStackSignature(error: Error): string {
    if (!error.stack) return 'no-stack';

    // Get first few lines of stack trace for signature
    const stackLines = error.stack.split('\n').slice(0, 3);
    return stackLines.join('|');
  }
}

/**
 * Context collection utility
 */
export class ContextCollector {
  /**
   * Collects basic error context
   */
  static collectErrorContext(): Partial<ErrorContext> {
    const context: Partial<ErrorContext> = {
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: Date.now(),
      sessionId: this.getSessionId(),
    };

    // Add performance metrics if available
    if (typeof performance !== 'undefined') {
      context.performance = this.collectPerformanceMetrics();
    }

    return context;
  }

  /**
   * Collects React-specific context
   */
  static collectReactContext(componentName?: string): Partial<ErrorContext> {
    return {
      ...this.collectErrorContext(),
      feature: this.getFeatureFromRoute(window.location.pathname),
      component: componentName,
    };
  }

  /**
   * Collects performance metrics
   */
  static collectPerformanceMetrics(): PerformanceMetrics {
    const metrics: PerformanceMetrics = {};

    // Navigation timing
    if (performance.getEntriesByType) {
      const navigation = performance.getEntriesByType(
        'navigation'
      )[0] as PerformanceNavigationTiming;
      if (navigation) {
        metrics.navigationTiming = navigation;
      }
    }

    // Memory usage (Chrome only)
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      metrics.memoryUsage = {
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
      };
    }

    // Connection type
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      metrics.connectionType = connection?.effectiveType || 'unknown';
    }

    return metrics;
  }

  /**
   * Creates an error breadcrumb
   */
  static createBreadcrumb(
    message: string,
    category: ErrorBreadcrumb['category'],
    level: ErrorBreadcrumb['level'] = 'info',
    data?: Record<string, any>
  ): ErrorBreadcrumb {
    return {
      timestamp: Date.now(),
      category,
      message,
      level,
      data,
    };
  }

  private static getSessionId(): string {
    // Try to get session ID from sessionStorage or generate one
    let sessionId = sessionStorage.getItem('error_logging_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('error_logging_session_id', sessionId);
    }
    return sessionId;
  }

  private static getFeatureFromRoute(pathname: string): string {
    // Extract feature name from route
    const segments = pathname.split('/').filter(Boolean);
    return segments[0] || 'home';
  }
}
